import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  School,
  Users,
  GraduationCap,
  IndianRupee,
  ClipboardList,
  BarChart3,
  Shield,
  Smartphone,
  Calendar,
  ChevronRight,
  Zap,
  Check,
  Clock,
  UserCheck,
  Headphones
} from 'lucide-react';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Student Management',
      description:
        'Complete student lifecycle from admission to graduation — digital records, admission approvals, and photo profiles, searchable in seconds.',
      size: 'lg' as const
    },
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: 'Teacher Management',
      description: 'Onboarding, documents, salary processing and performance tracking in one place.',
      size: 'md' as const
    },
    {
      icon: <IndianRupee className="w-6 h-6" />,
      title: 'Fee Management',
      description: 'Automated collection, receipts and payment tracking.',
      size: 'sm' as const
    },
    {
      icon: <ClipboardList className="w-6 h-6" />,
      title: 'Notice Board',
      description: 'Instant digital notices with priority levels.',
      size: 'sm' as const
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: 'Academic Planning',
      description: 'Academic calendar, class schedules and examinations, organized by term.',
      size: 'md' as const
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'Analytics & Reports',
      description: 'Dashboards for data-driven decisions.',
      size: 'sm' as const
    }
  ];

  const benefits = [
    { icon: <Zap className="w-[18px] h-[18px]" />, title: '100% Digital', description: 'Paperless school management, start to finish.' },
    { icon: <Shield className="w-[18px] h-[18px]" />, title: 'Secure & Reliable', description: 'Cloud-hosted on Firebase with enterprise-grade security.' },
    { icon: <Clock className="w-[18px] h-[18px]" />, title: '24/7 Access', description: 'Log in from the office, home, or your phone.' },
    { icon: <Headphones className="w-[18px] h-[18px]" />, title: 'Real Support', description: 'A team that answers when your school calls.' }
  ];

  const stats = [
    { number: '1000+', label: 'Schools' },
    { number: '50,000+', label: 'Students' },
    { number: '5,000+', label: 'Teachers' },
    { number: '99.9%', label: 'Uptime' }
  ];

  const sizeClasses: Record<'lg' | 'md' | 'sm', string> = {
    lg: 'col-span-12 lg:col-span-7 lg:row-span-2',
    md: 'col-span-12 sm:col-span-6 lg:col-span-5',
    sm: 'col-span-12 sm:col-span-6 lg:col-span-4'
  };

  return (
    <div className="min-h-screen bg-[#17140f] text-[#f3ede2]" style={{ fontVariantNumeric: 'tabular-nums' }}>
      {/* Header */}
      <header className="sticky top-0 z-20 bg-[#17140f]/85 backdrop-blur border-b border-[#3a3122]">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between py-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-[#e2916d] rounded-[10px] flex items-center justify-center flex-shrink-0">
              <School className="w-[18px] h-[18px] text-[#241408]" />
            </div>
            <span className="text-base font-bold">Kaksha360</span>
          </div>

          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-[#b9ad9b]">
            <a href="#features" className="hover:text-[#f3ede2] transition-colors">Features</a>
            <a href="#benefits" className="hover:text-[#f3ede2] transition-colors">Benefits</a>
            <a href="#mobile" className="hover:text-[#f3ede2] transition-colors">Mobile Apps</a>
          </nav>

          <div className="flex items-center gap-2.5">
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 text-sm font-semibold rounded-[10px] border border-[#3a3122] hover:border-[#e2916d] hover:text-[#e2916d] transition-colors"
            >
              School Login
            </button>
            <button
              onClick={() => navigate('/register')}
              className="px-4 py-2 text-sm font-semibold rounded-[10px] bg-[#e2916d] text-[#241408] hover:-translate-y-0.5 transition-transform"
            >
              Get Started
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-10 grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-11 items-center">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.08em] text-[#e2916d] mb-2.5">School Management Platform</p>
          <h1 className="text-[40px] sm:text-[44px] font-bold leading-[1.12] tracking-tight mb-4">
            Run your school<br />with <span className="text-[#e2916d]">one calm dashboard</span>.
          </h1>
          <p className="text-[17px] leading-relaxed text-[#b9ad9b] max-w-[46ch] mb-7">
            Kaksha360 replaces spreadsheets and paper registers with a single system for admissions, students, teachers, fees and notices — built for Indian schools.
          </p>

          <div className="flex flex-wrap gap-3 mb-8">
            <button
              onClick={() => navigate('/register')}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-[15px] font-semibold bg-[#e2916d] text-[#241408] hover:-translate-y-0.5 hover:shadow-[0_6px_16px_-6px_rgba(226,145,109,0.5)] transition-all"
            >
              <School className="w-[18px] h-[18px]" />
              Register Your School
            </button>
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-[15px] font-semibold border border-[#3a3122] hover:border-[#e2916d] hover:text-[#e2916d] transition-colors"
            >
              School Admin Login
              <ChevronRight className="w-[18px] h-[18px]" />
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-xs text-[#8a7f6e]">
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#4ade80]" />No setup fees</span>
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#4ade80]" />Cloud-hosted &amp; secure</span>
            <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#4ade80]" />Live in a day</span>
          </div>
        </div>

        {/* Product preview panel */}
        <div className="bg-[#211c15] border border-[#3a3122] rounded-[22px] p-4 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.55)]">
          <div className="flex items-center gap-1.5 px-1 pb-3.5">
            <span className="w-2 h-2 rounded-full bg-[#332b20]"></span>
            <span className="w-2 h-2 rounded-full bg-[#332b20]"></span>
            <span className="w-2 h-2 rounded-full bg-[#332b20]"></span>
          </div>
          <div className="grid grid-cols-[1.3fr_1fr] gap-2.5">
            <div className="row-span-2 bg-[#e2916d] text-[#241408] rounded-[14px] p-4 flex flex-col justify-between">
              <div>
                <p className="text-[11px] opacity-85">Monthly Fee Collection</p>
                <p className="text-xl font-bold mt-1">₹8,42,600</p>
              </div>
              <svg viewBox="0 0 160 28" className="w-full h-7 mt-2.5" preserveAspectRatio="none">
                <polyline
                  points="0,22 24,19 48,15 72,17 96,11 120,13 144,5"
                  fill="none"
                  stroke="rgba(36,20,8,0.75)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="bg-[#28221a] rounded-xl p-3">
              <div className="w-[22px] h-[22px] rounded-[6px] bg-[#332217] flex items-center justify-center mb-2">
                <Users className="w-3 h-3 text-[#e2916d]" />
              </div>
              <p className="text-[10px] text-[#8a7f6e]">Students</p>
              <p className="text-sm font-bold mt-0.5">1,284</p>
            </div>
            <div className="bg-[#28221a] rounded-xl p-3">
              <div className="w-[22px] h-[22px] rounded-[6px] bg-[#332217] flex items-center justify-center mb-2">
                <GraduationCap className="w-3 h-3 text-[#e2916d]" />
              </div>
              <p className="text-[10px] text-[#8a7f6e]">Teachers</p>
              <p className="text-sm font-bold mt-0.5">86</p>
            </div>
            <div className="col-span-2 bg-[#28221a] rounded-xl p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserCheck className="w-3.5 h-3.5 text-[#f0b64c]" />
                <div>
                  <p className="text-[13px] font-semibold">37 admissions</p>
                  <p className="text-[10px] text-[#8a7f6e]">awaiting review</p>
                </div>
              </div>
              <span className="text-[10px] font-bold text-[#f0b64c] bg-[#332711] px-2 py-1 rounded-full">Pending</span>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <div className="border-t border-b border-[#3a3122]">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => (
            <div
              key={index}
              className={`text-center py-6 px-4 ${index !== 0 ? 'border-l border-[#3a3122]' : ''} ${index === 2 ? 'lg:border-l border-l-0' : ''}`}
            >
              <div className="text-2xl font-bold text-[#e2916d] tabular-nums">{stat.number}</div>
              <div className="text-xs text-[#b9ad9b] mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Features */}
      <section id="features" className="max-w-6xl mx-auto px-6 py-20">
        <div className="max-w-xl mx-auto text-center mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.08em] text-[#e2916d] mb-2.5">Features</p>
          <h2 className="text-3xl font-bold mb-3">Everything a school office runs on</h2>
          <p className="text-[15.5px] text-[#b9ad9b] leading-relaxed">
            From the first admission form to the last fee receipt, one connected system.
          </p>
        </div>

        <div className="grid grid-cols-12 auto-rows-[minmax(150px,auto)] gap-3.5">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`${sizeClasses[feature.size]} rounded-[20px] p-6 flex flex-col transition-all duration-200 ease-snappy-out hover:-translate-y-0.5 ${
                feature.size === 'lg'
                  ? 'bg-[#e2916d] text-[#241408] justify-between'
                  : 'bg-[#211c15] border border-[#3a3122] hover:border-[#e2916d]'
              }`}
            >
              <div>
                <div
                  className={`flex items-center justify-center rounded-xl mb-4 flex-shrink-0 ${
                    feature.size === 'lg' ? 'w-[52px] h-[52px] bg-white/20' : 'w-11 h-11 bg-[#332217] text-[#e2916d]'
                  }`}
                >
                  {feature.icon}
                </div>
                <h3 className={`font-bold mb-2 ${feature.size === 'lg' ? 'text-xl' : 'text-[16.5px]'}`}>{feature.title}</h3>
                <p className={`leading-relaxed ${feature.size === 'lg' ? 'text-[14.5px] max-w-[38ch] text-[#241408]/80' : 'text-[13.5px] text-[#b9ad9b]'}`}>
                  {feature.description}
                </p>
              </div>
              {feature.size === 'lg' && (
                <div className="flex items-end gap-1.5 h-14 mt-auto">
                  {[40, 55, 35, 70, 50, 85].map((h, i) => (
                    <span
                      key={i}
                      style={{ height: `${h}%` }}
                      className={`flex-1 rounded-t-[3px] rounded-b-[1px] ${i === 5 ? 'bg-[#241408]' : 'bg-[#241408]/30'}`}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Benefits */}
      <section id="benefits" className="max-w-6xl mx-auto px-6 py-20">
        <div className="max-w-xl mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.08em] text-[#e2916d] mb-2.5">Why Kaksha360</p>
          <h2 className="text-3xl font-bold">Built for how Indian schools actually run</h2>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
          {benefits.map((benefit, index) => (
            <div key={index} className="bg-[#211c15] border border-[#3a3122] rounded-2xl p-5 flex flex-col gap-2.5">
              <div className="w-9 h-9 rounded-[10px] bg-[#173322] text-[#4ade80] flex items-center justify-center">
                {benefit.icon}
              </div>
              <h4 className="text-[14.5px] font-bold">{benefit.title}</h4>
              <p className="text-xs text-[#b9ad9b] leading-relaxed">{benefit.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Mobile apps teaser */}
      <section id="mobile" className="max-w-6xl mx-auto px-6 pb-20">
        <div className="bg-[#211c15] border border-[#3a3122] rounded-[20px] px-8 sm:px-10 py-9 flex items-center justify-between gap-8 flex-wrap">
          <div className="max-w-[460px]">
            <span className="inline-flex items-center gap-1.5 text-[11.5px] font-bold text-[#f0b64c] bg-[#332711] px-2.5 py-1 rounded-full mb-3.5">
              <Clock className="w-3 h-3" />
              In development
            </span>
            <h3 className="text-xl font-bold mb-2.5">Student &amp; parent apps are on the way</h3>
            <p className="text-[14.5px] text-[#b9ad9b] leading-relaxed">
              Real-time notifications, fee payments and assignment tracking from a phone — for iOS and Android. We'll email every registered school the moment it's ready.
            </p>
          </div>
          <div className="flex gap-3.5 flex-shrink-0">
            <div className="w-16 h-16 rounded-2xl bg-[#28221a] flex items-center justify-center">
              <Smartphone className="w-7 h-7 text-[#8a7f6e]" />
            </div>
            <div className="w-16 h-16 rounded-2xl bg-[#28221a] flex items-center justify-center">
              <Smartphone className="w-7 h-7 text-[#8a7f6e]" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <div className="bg-[#e2916d] text-[#241408] rounded-[28px] px-8 py-16 text-center">
          <h2 className="text-3xl font-bold mb-3.5">Ready to transform your school?</h2>
          <p className="text-[15.5px] max-w-[52ch] mx-auto mb-8 text-[#241408]/85 leading-relaxed">
            Join the schools already using Kaksha360 to streamline admissions, fees and communication.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => navigate('/register')}
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-[15px] font-semibold bg-[#241408] text-[#f3ede2] hover:-translate-y-0.5 transition-transform"
            >
              <School className="w-[18px] h-[18px]" />
              Register Your School
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#3a3122] pt-14 pb-7">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 lg:grid-cols-[1.6fr_1fr_1fr]">
            <div>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 bg-[#e2916d] rounded-[10px] flex items-center justify-center flex-shrink-0">
                  <School className="w-[18px] h-[18px] text-[#241408]" />
                </div>
                <span className="text-base font-bold">Kaksha360</span>
              </div>
              <p className="text-[13.5px] text-[#b9ad9b] leading-relaxed max-w-[36ch] mt-3.5">
                Empowering schools with digital tools for better education management and improved learning outcomes.
              </p>
            </div>

            <div>
              <h4 className="text-xs font-bold uppercase tracking-[0.04em] text-[#b9ad9b] mb-3.5">Features</h4>
              <ul className="flex flex-col gap-2.5">
                {['Student Management', 'Teacher Management', 'Fee Management', 'Notice Board', 'Analytics & Reports'].map((item) => (
                  <li key={item}>
                    <a href="#features" className="text-[13.5px] text-[#b9ad9b] hover:text-[#e2916d] transition-colors">{item}</a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-bold uppercase tracking-[0.04em] text-[#b9ad9b] mb-3.5">Support</h4>
              <ul className="flex flex-col gap-2.5">
                {['Help Center', 'Contact Support', 'Training Videos', 'Documentation', 'System Status'].map((item) => (
                  <li key={item}>
                    <a href="#" className="text-[13.5px] text-[#b9ad9b] hover:text-[#e2916d] transition-colors">{item}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t border-[#3a3122] mt-10 pt-6 text-center">
            <p className="text-xs text-[#8a7f6e]">© {new Date().getFullYear()} Kaksha360. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
